import type { HTMLAttributes, ReactNode } from 'react'
import { Trans } from '@lingui/react/macro'

import type { Form as FormHook, FormError } from '@jotta/hooks'
import { handleUnknownError } from '@jotta/types/AppError'

import { ErrorMessage } from '../../Elements/ErrorMessage/ErrorMessage'

type KeyAndError = [string, ReactNode]

type FormProps = {
  form: FormHook<any>
  header?: ReactNode
} & Exclude<HTMLAttributes<HTMLFormElement>, 'onSubmit'>

function asReactNode(error: FormError) {
  if (typeof error === 'string') {
    return error
  }
  const appError = handleUnknownError(error)
  return appError.view || appError.message
}

export function Form({ form, header, children, ...props }: FormProps) {
  const { error, status, fields } = form

  const errors: KeyAndError[] = []
  if (error) {
    errors.push(['form_error', asReactNode(error)])
  }

  const fieldsWithSummaryError: KeyAndError[] = Object.keys(fields).flatMap(
    key => {
      const field = fields[key]
      const error = field.error
      return status === 'invalid' && error && error.displayInSummary
        ? [[key, error.view]]
        : []
    },
  )
  // show field errors in summary only if summary will display at least 2 errors in total
  if (fieldsWithSummaryError.length + errors.length >= 2) {
    errors.push(...fieldsWithSummaryError)
  }

  const errorCount = errors.length
  return (
    <form onSubmit={form.onSubmit} onReset={form.onReset} {...props}>
      {header}

      {errorCount > 0 && (
        <ErrorMessage className="!mb-4">
          {errorCount == 1 && errors[0][1]}
          {errorCount > 1 && (
            <>
              <div className="pb-4">
                <Trans>
                  Oops! Some details are missing from the form below
                </Trans>
              </div>
              <ul className="body-sm-ln list-inside list-disc pb-4 pt-1 text-grey2">
                {errors.map(([key, reactNode]) => (
                  <li key={key}>{reactNode}</li>
                ))}
              </ul>
            </>
          )}
        </ErrorMessage>
      )}

      {children}
    </form>
  )
}
